import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Camera, Images, Trash2, Sparkles } from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { usePoints } from '../timeline/usePoints'
import { useAddMedia, useDeleteMedia, useMedia } from '../live/useToday'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { cn } from '../../shared/lib/cn'
import type { MediaItem } from '../../shared/api/types'
import AutoAlbumsPanel from './AutoAlbumsPanel'

type Filter = 'all' | 'photo' | 'video' | 'unbound'

export function MediaLibrary() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const trip = useTrip(id)
  const points = usePoints(id)
  const media = useMedia(id)
  const add = useAddMedia(tripId)
  const remove = useDeleteMedia(tripId)
  const fileRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [albumsOpen, setAlbumsOpen] = useState(false)
  const [albumToast, setAlbumToast] = useState<string | null>(null)

  const pointTitles = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of points.data ?? []) m.set(p.id, p.title)
    return m
  }, [points.data])

  const filtered = useMemo(() => {
    const items = media.data ?? []
    return items.filter((m) => {
      if (filter === 'photo') return m.type === 'photo'
      if (filter === 'video') return m.type === 'video'
      if (filter === 'unbound') return !m.point_id
      return true
    })
  }, [media.data, filter])

  const onPick = () => fileRef.current?.click()
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) add.mutate({ file })
    e.target.value = ''
  }

  if (media.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
            <Images className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold text-st-text">Media</h1>
            <p className="text-sm text-st-muted">{trip.data?.title}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setAlbumsOpen(true)}>
            <Sparkles className="h-4 w-4" /> AI Albums
          </Button>
          <Button variant="outline" onClick={onPick} disabled={add.isPending}>
            <Camera className="h-4 w-4" /> {add.isPending ? 'Uploading…' : 'Add photo'}
          </Button>
          <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={onChange} />
        </div>
      </div>

      {albumToast && (
        <p className="mb-3 rounded-st-md bg-st-primary/10 px-3 py-2 text-sm text-st-primary">
          {albumToast}
        </p>
      )}

      <FilterChips
        options={[
          { id: 'all', label: 'All' },
          { id: 'photo', label: 'Photos' },
          { id: 'video', label: 'Videos' },
          { id: 'unbound', label: 'Trip only' },
        ]}
        value={filter}
        onChange={setFilter}
      />

      {filtered.length === 0 ? (
        <EmptyState
          Icon={Images}
          title="No media yet"
          description="Add photos from a place on your route or upload trip-wide shots here."
        />
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {filtered.map((m) => (
            <MediaTile
              key={m.id}
              item={m}
              placeLabel={m.point_id ? pointTitles.get(m.point_id) : null}
              onDelete={() => remove.mutate(m.id)}
            />
          ))}
        </div>
      )}

      {albumsOpen && (
        <AutoAlbumsPanel
          tripId={tripId}
          onClose={() => setAlbumsOpen(false)}
          onApplied={(count) => {
            setAlbumToast(`${count} photo${count === 1 ? '' : 's'} added to your web story`)
            setTimeout(() => setAlbumToast(null), 4000)
          }}
        />
      )}
    </div>
  )
}

function FilterChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={cn(
            'rounded-st-full px-3 py-1 text-xs font-medium transition',
            value === o.id ? 'bg-st-primary text-white' : 'bg-st-surface-2 text-st-muted hover:text-st-text',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function MediaTile({
  item,
  placeLabel,
  onDelete,
}: {
  item: MediaItem
  placeLabel?: string | null
  onDelete: () => void
}) {
  return (
    <div className="group relative overflow-hidden rounded-st-md border border-st-line bg-st-surface-2">
      <div className="aspect-square">
        {item.type === 'video' ? (
          <video src={item.url} className="h-full w-full object-cover" controls />
        ) : (
          <img src={item.url} alt="" className="h-full w-full object-cover" />
        )}
      </div>
      <div className="p-2">
        <p className="truncate text-xs text-st-muted">{placeLabel ?? 'Trip-wide'}</p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1 top-1 rounded-st-sm bg-black/50 p-1 text-white opacity-0 transition group-hover:opacity-100"
        aria-label="Delete"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export { FilterChips }
